import PageShell from "../components/PageShell";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="mb-10">
    <h2 className="font-cinzel text-amber-200 text-xl sm:text-2xl tracking-wide mb-4">
      {title}
    </h2>
    <div className="text-stone-300 text-[15px] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <PageShell testId="privacy-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="text-amber-300/80 text-xs tracking-[0.25em] uppercase mb-4">
          Legal · IRL Stats
        </p>
        <h1
          data-testid="privacy-heading"
          className="font-cinzel text-4xl sm:text-5xl text-amber-50 leading-tight mb-3"
        >
          Privacy Policy
        </h1>
        <p className="text-stone-500 text-sm mb-12">
          Effective Date: May 21, 2026
        </p>

        <Section title="1. Who We Are" testId="privacy-section-intro">
          <p>
            This Privacy Policy describes how <strong>The Legacy Labs LLC</strong>,
            doing business as <strong>IRL Stats</strong> ("IRL Stats," "we,"
            "us," or "our"), collects, uses, and protects information when you
            use the IRL Stats mobile application and related services
            (collectively, the "Service").
          </p>
          <p>
            By using the Service, you agree to the practices described in this
            Privacy Policy. If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section title="2. Information We Collect" testId="privacy-section-collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>
              <strong>Account information</strong> — your name, email address,
              username, password (hashed), and date of birth.
            </li>
            <li>
              <strong>Fitness data</strong> — workout logs, exercises performed,
              sets, reps, weight, duration, XP/progression stats, goals, and
              achievements.
            </li>
            <li>
              <strong>Location data</strong> — GPS/location data if you enable
              location-based features (such as run/route tracking). Location
              access is optional and can be revoked at any time in your device
              settings.
            </li>
            <li>
              <strong>Subscription and payment metadata</strong> — subscription
              status, plan, renewal dates, and transaction identifiers. Payment
              processing is handled by Apple App Store; we do not receive or
              store your full payment card details.
            </li>
            <li>
              <strong>Device and app analytics</strong> — device model,
              operating system, app version, crash logs, performance metrics,
              and anonymized usage events used to diagnose issues and improve
              the Service.
            </li>
            <li>
              <strong>Support requests</strong> — information you provide when
              you contact us, including the contents of your messages and
              attachments.
            </li>
          </ul>
        </Section>

        <Section title="3. How We Use Information" testId="privacy-section-use">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>Operate, maintain, and provide the features of the Service;</li>
            <li>
              Calculate XP, levels, personal records, achievements, and other
              progression elements;
            </li>
            <li>
              Process and manage subscriptions, renewals, and entitlements;
            </li>
            <li>
              Diagnose bugs, monitor performance, and improve the Service;
            </li>
            <li>
              Respond to your support requests and communicate with you about
              the Service;
            </li>
            <li>
              Detect, prevent, and address fraud, abuse, and security incidents;
            </li>
            <li>Comply with applicable laws and enforce our Terms of Service.</li>
          </ul>
        </Section>

        <Section title="4. How We Share Information" testId="privacy-section-share">
          <p>
            We do not sell your personal information. We may share information
            only as follows:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>
              <strong>Service providers</strong> who help us operate the
              Service (e.g., hosting, analytics, crash reporting, customer
              support), under contractual obligations of confidentiality;
            </li>
            <li>
              <strong>Co-op / social features</strong> — information you
              voluntarily share with other users (e.g., during co-op workouts)
              will be visible to those users;
            </li>
            <li>
              <strong>Legal requirements</strong> — when required by law,
              subpoena, or to protect our rights, users, or the public;
            </li>
            <li>
              <strong>Business transfers</strong> — in connection with a
              merger, acquisition, or sale of assets, subject to standard
              confidentiality protections.
            </li>
          </ul>
        </Section>

        <Section title="5. Data Retention" testId="privacy-section-retention">
          <p>
            We retain your information for as long as your account is active or
            as needed to provide the Service. You may request deletion of your
            account and associated data at any time by emailing{" "}
            <a
              href="mailto:irlstatsapp@gmail.com"
              className="text-amber-300 underline-offset-2 hover:underline"
            >
              irlstatsapp@gmail.com
            </a>
            . We may retain certain information as required by law or for
            legitimate business purposes (e.g., fraud prevention, accounting).
          </p>
        </Section>

        <Section title="6. Your Choices and Rights" testId="privacy-section-rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-stone-300">
            <li>Access, correct, or delete the information we hold about you;</li>
            <li>Object to or restrict certain processing of your information;</li>
            <li>Withdraw consent where processing is based on consent;</li>
            <li>Disable location access via your device settings;</li>
            <li>
              File a complaint with a data protection authority in your region.
            </li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a
              href="mailto:irlstatsapp@gmail.com"
              className="text-amber-300 underline-offset-2 hover:underline"
            >
              irlstatsapp@gmail.com
            </a>
            .
          </p>
        </Section>

        <Section title="7. Children's Privacy" testId="privacy-section-children">
          <p>
            The Service is intended for users <strong>13 years of age and
            older</strong>. Users under 18 must have the consent of a parent or
            legal guardian to use the Service. We do not knowingly collect
            personal information from children under 13. If we learn we have
            collected such information, we will delete it promptly.
          </p>
        </Section>

        <Section title="8. Security" testId="privacy-section-security">
          <p>
            We use reasonable administrative, technical, and physical safeguards
            designed to protect your information. However, no method of
            transmission or storage is completely secure, and we cannot
            guarantee absolute security.
          </p>
        </Section>

        <Section title="9. International Users" testId="privacy-section-international">
          <p>
            The Service is operated from the United States. If you access the
            Service from outside the U.S., your information may be transferred
            to, stored in, and processed in the U.S. or other jurisdictions.
          </p>
        </Section>

        <Section title="10. Changes to This Policy" testId="privacy-section-changes">
          <p>
            We may update this Privacy Policy from time to time. We will post
            the updated version with a new effective date. Material changes will
            be communicated through the Service or by email.
          </p>
        </Section>

        <Section title="11. Contact Us" testId="privacy-section-contact">
          <p>
            For questions about this Privacy Policy or our privacy practices,
            contact us at:
          </p>
          <p className="text-amber-200">
            The Legacy Labs LLC, dba IRL Stats
            <br />
            Email:{" "}
            <a
              href="mailto:irlstatsapp@gmail.com"
              className="text-amber-300 underline-offset-2 hover:underline"
            >
              irlstatsapp@gmail.com
            </a>
          </p>
        </Section>
      </div>
    </PageShell>
  );
};

export default Privacy;
